// The navigation panel (docs/UI-POLISH.md decision 4): the game switcher, the area's title, the branch or environment
// picker, then the area's pages; the Content area adds the Documents template tree. Replaces the single sidebar.
// Under the branch selector, the release strip (spec/dashboard.md section 3.3): what each environment serves of this branch.
import { useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { ops } from '../api/operations';
import type { Template } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { IconButton } from '../ui/Button';
import { ChevronDown, ChevronRight, ChevronsRight, Gamepad2, PanelLeftClose, SlidersHorizontal, FileText } from '../ui/icons';
import { Switch, TextInput } from '../ui/inputs';
import { Popover } from '../ui/Popover';
import { useQuery } from '../workspace/cache';
import { areaByKey, itemHref, itemIcons, type AreaKey } from './areas';
import { BranchSelector } from './BranchSelector';
import { defaultEnv } from './envMemory';
import { ReleaseStatus } from './ReleaseStatusStrip';
import { useGame } from './GameContext';
import { isSystemTemplate, routes, type SidebarItem } from './routes';

export interface NavPanelProps {
  area: AreaKey | null;
  branch: string | null;
  env: string | null;
  head: string | null;
  onCollapse: () => void;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const s = words.length >= 2 ? `${words[0]![0]}${words[1]![0]}` : name.slice(0, 2);
  return s.toUpperCase() || '·';
}

/** The game switcher at the top of the panel: the games of the organization, the organizations. */
function GameSwitcher() {
  const { org, game } = useGame();
  const { client } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const gamesQ = useQuery(anchor ? `org:${org}:games:nav` : null, () => client.games(org));
  return (
    <>
      <button type="button" className="game-switch" onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="dialog" aria-expanded={anchor !== null} aria-label={`Game: ${game.name}. Switch game`} title={game.name}>
        <span className="game-tile" aria-hidden="true">
          {initials(game.name)}
        </span>
        <span className="game-text">
          <span className="name">{game.name}</span>
          <span className="org">{org}</span>
        </span>
        <ChevronDown aria-hidden="true" />
      </button>
      <Popover anchor={anchor} open={anchor !== null} onClose={() => setAnchor(null)} label="Games">
        <div className="muted small" style={{ padding: '0 4px 2px' }}>
          Games of {org}
        </div>
        <div className="game-list">
          {gamesQ.loading && !gamesQ.data && <span className="loading-line" style={{ padding: 8 }}>Loading…</span>}
          {(gamesQ.data?.items ?? []).map((g) => (
            <Link key={g.id} to={routes.game(org, g.slug)} className={`game-row ${g.slug === game.slug ? 'active' : ''}`} onClick={() => setAnchor(null)} aria-current={g.slug === game.slug ? 'page' : undefined}>
              <span className="game-tile" aria-hidden="true">
                {initials(g.name)}
              </span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
            </Link>
          ))}
        </div>
        <div className="menu-sep" role="separator" />
        <Link to={routes.games(org)} className="game-row" onClick={() => setAnchor(null)}>
          <Gamepad2 aria-hidden="true" style={{ width: 14, height: 14 }} />
          All games
        </Link>
        <Link to={routes.orgs()} className="game-row" onClick={() => setAnchor(null)}>
          <ChevronsRight aria-hidden="true" style={{ width: 14, height: 14 }} />
          Organizations
        </Link>
      </Popover>
    </>
  );
}

/** The environment switch of the environment-scoped pages: rewrites the environment segment, keeps the page. */
function EnvSwitch({ env, page }: { env: string; page: string }) {
  const { org, game, environments } = useGame();
  return (
    <div className="env-switch" role="group" aria-label="Environment">
      {environments.map((e) => (
        <NavLink key={e.id} to={routes.env(org, game.slug, e.slug, page)} className={() => (e.slug === env ? 'active' : '')} aria-current={e.slug === env ? 'true' : undefined} title={e.name}>
          {e.kind}
        </NavLink>
      ))}
    </div>
  );
}

function Items({ items, href, filter }: { items: SidebarItem[]; href: (item: SidebarItem) => string; filter: string }) {
  const shown = items.filter((i) => i.label.toLowerCase().includes(filter));
  if (shown.length === 0) return null;
  return (
    <ul>
      {shown.map((item) => {
        const Icon = itemIcons[item.key];
        return (
          <li key={item.key}>
            <NavLink to={href(item)} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
              {Icon && <Icon aria-hidden="true" />}
              <span>{item.label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function Section({ title, action, children, defaultOpen = true }: { title: string; action?: ReactNode; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="panel-section">
      <div className="panel-section-head">
        <button type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <span>{title}</span>
          {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
        </button>
        {action}
      </div>
      {open && children}
    </div>
  );
}

export function NavPanel({ area, branch, env, head, onCollapse }: NavPanelProps) {
  const { org, game, environments, branches } = useGame();
  const { client } = useAuth();
  const location = useLocation();
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const f = filter.trim().toLowerCase();
  // without an environment in the URL, the environment switch and the environment links default to the one last worked in
  const envSlug = env ?? defaultEnv(game.id, environments);
  // an environment page has no branch in its URL: its branch links lead to the default branch (section 9.11–9.12)
  const b = branch ?? branches.find((x) => x.isDefault)?.name ?? branches[0]?.name ?? 'main';
  const shown = areaByKey(area ?? 'content');
  const href = (item: SidebarItem) => itemHref(item, { org, game: game.slug, branch: b, env: envSlug });
  const envPage = env ? (location.pathname.split(`/envs/${encodeURIComponent(env)}/`)[1]?.split('/')[0] ?? 'players') : 'players';

  const templatesQ = useQuery(shown.key === 'content' && b ? `branch:${game.id}:${b}:templates` : null, () => client.all<Template>((cursor) => client.data(ops.listTemplates, { path: { org, game: game.slug, branch: b }, query: { limit: 200, cursor } })), { head });
  const documentTemplates = useMemo(() => {
    const all = templatesQ.data ?? [];
    const visible = all.filter((t) => (showAll ? true : t.kind === 'document' && !isSystemTemplate(t.name) && !t.flags.includes('hide_from_navigation') && !t.flags.includes('internal') && !t.flags.includes('abstract')));
    const byNs = new Map<string, Template[]>();
    for (const t of visible) {
      const parts = t.name.split('.');
      const ns = parts.length > 1 ? parts.slice(0, -1).join('.') : '';
      const list = byNs.get(ns) ?? [];
      list.push(t);
      byNs.set(ns, list);
    }
    return Array.from(byNs.entries()).sort(([a], [c]) => a.localeCompare(c));
  }, [templatesQ.data, showAll]);

  const branchScoped = shown.key === 'content' || shown.key === 'liveops' || shown.key === 'releases';
  return (
    <nav className="panel" aria-label="Game navigation">
      <div className="panel-head">
        <GameSwitcher />
        <div className="panel-title-row">
          <span className="panel-title">{shown.label}</span>
          <span className="row" style={{ gap: 2 }}>
            {shown.key === 'liveops' && (
              <Link to={`${routes.branch(org, game.slug, b, 'liveops/store')}?dialog=liveops-settings`} className="icon-button" aria-label="LiveOps settings" title="LiveOps settings: the comfort payment">
                <SlidersHorizontal aria-hidden="true" />
              </Link>
            )}
            <IconButton label="Hide navigation" onClick={onCollapse}>
              <PanelLeftClose aria-hidden="true" />
            </IconButton>
          </span>
        </div>
        {branchScoped && <BranchSelector current={branch} />}
        {/* not on the Releases area: its Environments tab is the full form of the strip */}
        {branchScoped && shown.key !== 'releases' && <ReleaseStatus branch={branch} head={head} />}
        {shown.key === 'players' && <EnvSwitch env={envSlug} page={envPage} />}
      </div>
      <div className="panel-body">
        {shown.key === 'content' && (
          <div className="panel-filter">
            <TextInput size="s" aria-label="Filter menu" placeholder="Filter menu…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
        )}
        {shown.key === 'settings' ? (
          <>
            <div className="panel-section">
              <Items items={shown.items.filter((i) => i.scope !== 'env')} href={href} filter={f} />
            </div>
            <Section title="Environment">
              <div style={{ padding: '2px 4px 6px' }}>
                <EnvSwitch env={envSlug} page={env && envPage !== 'players' && envPage !== 'orders' ? envPage : 'settings'} />
              </div>
              <Items items={shown.items.filter((i) => i.scope === 'env')} href={href} filter={f} />
            </Section>
          </>
        ) : (
          <div className="panel-section">
            <Items items={shown.items} href={href} filter={f} />
          </div>
        )}
        {shown.key === 'content' && (
          <Section title="Documents" action={<Switch aria-label="All templates" label="All" checked={showAll} onChange={setShowAll} />}>
            {templatesQ.loading && !templatesQ.data && <div className="panel-note">Loading…</div>}
            {!templatesQ.loading && documentTemplates.length === 0 && <div className="panel-note">{showAll ? 'No templates on this branch.' : 'No document templates yet.'}</div>}
            {documentTemplates.map(([ns, list]) => {
              const items = list.filter((t) => (t.displayName ?? t.name).toLowerCase().includes(f) || t.name.toLowerCase().includes(f));
              if (items.length === 0) return null;
              return (
                <div key={ns || '_'} className="nav-sub">
                  {ns && <div className="nav-ns">{ns}</div>}
                  <ul>
                    {items.map((t) => (
                      <li key={t.id}>
                        <NavLink to={routes.documents(org, game.slug, b, t.name)} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title={t.name}>
                          <FileText aria-hidden="true" />
                          <span>{t.displayName ?? t.name.split('.').pop()}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </Section>
        )}
      </div>
    </nav>
  );
}
